import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useSearchParams } from 'react-router-dom';
import { DynamicForm } from '@shared/components/DynamicForm';
import { FormField } from '@shared/services/FormServices';
import { ValueLabel } from '@features/ax/shared/ValueLabel';
import { Badge } from '@shared/ui/badge';
import { Button } from '@shared/ui/button';
import { Wrench, Globe, Package, Key, ArrowRight, CheckCircle2, Box, Clock } from 'lucide-react';
import { FIELD_CLASSES } from '@shared/utils/constants';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { useWarehouses, useChoices } from '@features/mx/hooks';
import { useCompleteRepair, useReturnToInventory } from '@features/mx/operations/hooks';
import { useToast } from '@shared/hooks/use-toast';

interface RepairProps {
  operation?: any;
  isOperationStarted?: boolean;
  onStartOperation?: () => void;
  originFrom?: string;
}

const PHYSICAL_STATUS_MAP: Record<string, string> = {
  '0': 'Installed',
  '1': 'In Transit',
  '2': 'In Warehouse',
  '3': 'Under Repair',
  '4': 'Repaired',
  '5': 'Retired',
  '6': 'Scrapped',
};

const formatPhysicalStatus = (val: any, options?: { label: string; value: string }[]) => {
  if (val == null || val === '' || val === 'NA') return 'NA';
  const strVal = String(val).trim();
  if (options && options.length > 0) {
    const found = options.find((opt) => String(opt.value) === strVal || opt.label.toLowerCase() === strVal.toLowerCase());
    if (found) return found.label;
  }
  return PHYSICAL_STATUS_MAP[strVal] || strVal;
};

export const Repair: React.FC<RepairProps> = ({
  operation,
  isOperationStarted = false,
  onStartOperation,
  originFrom,
}) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const effectiveFrom = originFrom || searchParams.get('from') || (location.state as any)?.from || undefined;
  const isFromHistory = effectiveFrom === 'history';
  const isFromPending = effectiveFrom === 'pending';

  console.log({
    isFromHistory,
    isFromPending,
  });

  const activeResourceCode =
    operation?.resourceOverview?.resourceId ||
    operation?.resourceCard?.resourceId ||
    operation?.resourceOverview?.resource_id ||
    operation?.resourceCode ||
    'NA';

  const activeResourceType =
    operation?.resourceOverview?.resourceTypeDisplay ||
    operation?.resourceCard?.type ||
    operation?.resourceOverview?.resource_type_display ||
    'NA';

  const activeResourceSubtype =
    operation?.resourceOverview?.resourceSubTypeDisplay ||
    operation?.resourceCard?.subType ||
    operation?.resourceOverview?.resource_sub_type_display ||
    'NA';

  const statusStr = String(
    operation?.statusDisplay ||
    operation?.status_display ||
    operation?.statusName ||
    operation?.status_name ||
    ''
  ).toLowerCase().trim();

  const isRepairedInInventory =
    statusStr === 'repaired in inventory' ||
    statusStr.includes('repaired in inventory') ||
    statusStr.includes('repaired-in-inventory');

  const shouldShowCompleted = isFromHistory || isRepairedInInventory;

  const remoteUtilityId = useRemoteUtilityId() || '';
  const { mutate: completeRepair, isPending: isCompleting } = useCompleteRepair();
  const { mutate: returnInventory, isPending: isReturning } = useReturnToInventory();
  const { toast } = useToast();
  // Steps: 'form' -> 'inventory' -> 'completed'
  const [step, setStep] = useState<'form' | 'inventory' | 'completed'>('form');

  const [localStarted, setLocalStarted] = useState(false);
  const isStarted = isOperationStarted || localStarted || operation?.statusDisplay === 'In Progress';

  const handleStart = () => {
    setLocalStarted(true);
    onStartOperation?.();
  };

  // 1. Initial Repair Findings Form
  const repairForm = useForm({
    defaultValues: {
      faultType: '',
      repairMethod: '',
      partsReplaced: '',
      faultDescription: '',
      technicianNotes: '',
    },
  });

  const [repairOutcome, setRepairOutcome] = useState({
    faultType: '',
    repairMethod: '',
    partsReplaced: '',
    faultDescription: '',
    technicianNotes: '',
    resourceCode: activeResourceCode,
    resourceName: activeResourceType,
    resourceType: activeResourceType,
  });

  const inventoryForm = useForm({
    defaultValues: {
      warehouseId: '',
      physicalStatus: '',
    },
  });

  const [inventoryRecord, setInventoryRecord] = useState({
    warehouseName: '',
    physicalStatus: '',
  });

  const selectedWarehouseId = inventoryForm.watch('warehouseId');

  // Fetch warehouses
  const { data: warehousesResponse } = useWarehouses(remoteUtilityId);
  const { data: physicalStatusChoices } = useChoices({
    choice: 'physical_status',
    remoteUtilityId,
  });

  const warehouseOptions = useMemo(() => {
    const results = warehousesResponse?.results || warehousesResponse?.result || warehousesResponse || [];
    if (Array.isArray(results) && results.length > 0) {
      return results.map((item: any) => ({
        label: item.name || item.warehouse_name || item.code,
        value: String(item.id || item.code),
        location: item.location || item.address || 'Building A, Shelf R1',
      }));
    }
    return [
      { label: 'Warehouse 1 – Main Storage', value: '1', location: 'Building A, Shelf R1' },
      { label: 'Warehouse 2 – North Yard', value: '2', location: 'Building B, Section 4' },
      { label: 'Central Depository', value: '3', location: 'Main Hub, Bay 2' },
    ];
  }, [warehousesResponse]);

  const physicalStatusOptions = useMemo(() => {
    const choices = (physicalStatusChoices as any)?.result || physicalStatusChoices;
    return Array.isArray(choices)
      ? choices.map((item: any) => ({
        label: item.value,
        value: String(item.key),
      }))
      : [
        { label: 'Repaired', value: '4' },
        { label: 'In Warehouse', value: '2' },
        { label: 'Installed', value: '0' },
        { label: 'Retired', value: '5' },
      ];
  }, [physicalStatusChoices]);

  const selectedWarehouse = useMemo(() => {
    if (!selectedWarehouseId) return null;
    return (
      warehouseOptions.find((w) => String(w.value) === String(selectedWarehouseId)) || null
    );
  }, [warehouseOptions, selectedWarehouseId]);

  const initialFields: FormField[] = [
    {
      name: 'faultType',
      label: 'Fault Type',
      type: 'select',
      required: true,
      placeholder: 'Select fault type...',
      classes: FIELD_CLASSES,
      className: 'col-span-1',
      options: [
        { label: 'Electrical Fault', value: 'Electrical Fault' },
        { label: 'Mechanical Failure', value: 'Mechanical Failure' },
        { label: 'Communication Error', value: 'Communication Error' },
        { label: 'Physical Damage', value: 'Physical Damage' },
        { label: 'Calibration Issue', value: 'Calibration Issue' },
        { label: 'Leak', value: 'Leak' },
        { label: 'Corrosion', value: 'Corrosion' },
        { label: 'Other', value: 'Other' },
      ],
    },
    {
      name: 'repairMethod',
      label: 'Repair Method',
      type: 'select',
      placeholder: 'Select repair method...',
      classes: FIELD_CLASSES,
      className: 'col-span-1',
      options: [
        { label: 'On-site Repair', value: 'On-site Repair' },
        { label: 'Remote Fix', value: 'Remote Fix' },
        { label: 'Component Replacement', value: 'Component Replacement' },
        { label: 'Calibration', value: 'Calibration' },
        { label: 'Seal Replacement', value: 'Seal Replacement' },
        { label: 'Full Overhaul', value: 'Full Overhaul' },
      ],
    },
    {
      name: 'partsReplaced',
      label: 'Parts Replaced',
      type: 'text',
      placeholder: 'e.g. Battery pack, pressure valve gasket',
      classes: FIELD_CLASSES,
      className: 'col-span-2',
    },
    {
      name: 'faultDescription',
      label: 'Fault Description',
      type: 'textarea',
      placeholder: 'Describe the issue found during inspection...',
      classes: FIELD_CLASSES,
      className: 'col-span-1',
    },
    {
      name: 'technicianNotes',
      label: 'Technician Notes',
      type: 'textarea',
      placeholder: 'Additional notes or follow-up recommendations...',
      classes: FIELD_CLASSES,
      className: 'col-span-1',
    },
  ];

  const inventoryFields: FormField[] = [
    {
      name: 'warehouseId',
      label: 'Warehouse',
      type: 'select',
      placeholder: 'Select warehouse...',
      classes: FIELD_CLASSES,
      className: 'col-span-1',
      options: warehouseOptions.map((w) => ({ label: w.label, value: w.value })),
    },
    {
      name: 'physicalStatus',
      label: 'Physical Status',
      type: 'select',
      placeholder: 'Select physical status...',
      classes: FIELD_CLASSES,
      className: 'col-span-1',
      options: physicalStatusOptions,
    },
  ];

  const handleInitialSubmit = (data: any) => {
    const payload = {
      fault_type: data.faultType,
      repair_method: data.repairMethod,
      fault_description: data.faultDescription,
      parts_replaced: data.partsReplaced,
      technician_notes: data.technicianNotes,
    };

    if (operation?.id) {
      completeRepair({ operationId: operation.id, payload }, {
        onSuccess: () => {
          setRepairOutcome((prev) => ({
            ...prev,
            faultType: data.faultType,
            repairMethod: data.repairMethod,
            partsReplaced: data.partsReplaced,
            faultDescription: data.faultDescription,
            technicianNotes: data.technicianNotes,
          }));
          setStep('inventory');
          toast({ title: 'Success', description: 'Repair findings saved.' });
        },
        onError: (err: any) => {
          const apiMsg = err?.response?.data?.message || err?.message || 'Failed to save repair findings.';
          toast({ title: 'Error', description: apiMsg, variant: 'destructive' });
        }
      });
    } else {
      setRepairOutcome((prev) => ({
        ...prev,
        faultType: data.faultType,
        repairMethod: data.repairMethod,
        partsReplaced: data.partsReplaced,
        faultDescription: data.faultDescription,
        technicianNotes: data.technicianNotes,
      }));
      setStep('inventory');
    }
  };

  const handleInventorySubmit = (data: any) => {
    const chosenWh = warehouseOptions.find((w) => w.value === data.warehouseId) || selectedWarehouse;
    const payload = {
      warehouse_id: Number(data.warehouseId),
      physical_status: Number(data.physicalStatus),
    };

    if (operation?.id) {
      returnInventory({ operationId: operation.id, payload }, {
        onSuccess: () => {
          setInventoryRecord({
            warehouseName: chosenWh?.label || '',
            physicalStatus: data.physicalStatus,
          });
          setStep('completed');
          toast({ title: 'Success', description: 'Returned to inventory.' });
        },
        onError: (err: any) => {
          const apiMsg = err?.response?.data?.message || err?.message || 'Failed to return to inventory.';
          toast({ title: 'Error', description: apiMsg, variant: 'destructive' });
        }
      });
    } else {
      setInventoryRecord({
        warehouseName: chosenWh?.label || '',
        physicalStatus: data.physicalStatus,
      });
      setStep('completed');
    }
  };

  let detailData = operation?.detailData || operation?.detail_data;
  if (typeof detailData === 'string') {
    try {
      detailData = JSON.parse(detailData);
    } catch (e) {
    }
  }
  detailData = detailData || {};

  const outcomeFaultType =
    repairOutcome.faultType ||
    detailData?.faultType ||
    detailData?.fault_type ||
    operation?.faultType ||
    operation?.fault_type ||
    'NA';

  const outcomeRepairMethod =
    repairOutcome.repairMethod ||
    detailData?.repairMethod ||
    detailData?.repair_method ||
    detailData?.method ||
    operation?.repairMethod ||
    operation?.repair_method ||
    'NA';

  const outcomePartsReplaced =
    repairOutcome.partsReplaced ||
    detailData?.partsReplaced ||
    detailData?.parts_replaced ||
    operation?.partsReplaced ||
    operation?.parts_replaced ||
    '';

  const outcomeFaultDescription =
    repairOutcome.faultDescription ||
    detailData?.faultDescription ||
    detailData?.fault_description ||
    operation?.faultDescription ||
    operation?.fault_description ||
    'NA';

  const outcomeTechnicianNotes =
    repairOutcome.technicianNotes ||
    detailData?.technicianNotes ||
    detailData?.technician_notes ||
    detailData?.notes ||
    operation?.technicianNotes ||
    operation?.technician_notes ||
    operation?.notes ||
    '';

  const outcomeReturnedTo =
    inventoryRecord.warehouseName ||
    detailData?.returnedTo ||
    detailData?.returned_to ||
    detailData?.returnWarehouseName ||
    detailData?.return_warehouse_name ||
    detailData?.warehouseName ||
    detailData?.warehouse_name ||
    detailData?.warehouse?.name ||
    detailData?.warehouse ||
    detailData?.returnWarehouse ||
    detailData?.return_warehouse ||
    operation?.warehouseName ||
    operation?.warehouse_name ||
    'NA';

  const outcomeRawStatus =
    inventoryRecord.physicalStatus ||
    detailData?.physicalStatusDisplay ||
    detailData?.physical_status_display ||
    detailData?.physicalStatus ||
    detailData?.physical_status ||
    detailData?.returnPhysicalStatusDisplay ||
    detailData?.return_physical_status_display ||
    detailData?.returnPhysicalStatus ||
    detailData?.return_physical_status ||
    operation?.physicalStatusDisplay ||
    operation?.physical_status_display ||
    operation?.physicalStatus ||
    operation?.physical_status ||
    'NA';

  const outcomePhysicalStatus = formatPhysicalStatus(outcomeRawStatus, physicalStatusOptions);

  return (
    <div className="space-y-5 pt-2">
      {/* Top Resource Banner */}
      <div className="bg-amber-50/40 border border-amber-200/70 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 font-medium">Repairing:</span>
          <span className="font-semibold text-amber-800">{activeResourceCode}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="bg-amber-50 text-amber-700 border-amber-200 text-xs font-medium px-2.5 py-0.5 rounded-full"
          >
            {activeResourceType}
          </Badge>
          {activeResourceSubtype !== 'NA' && (
            <Badge
              variant="outline"
              className="bg-amber-50 text-amber-700 border-amber-200 text-xs font-medium px-2.5 py-0.5 rounded-full"
            >
              {activeResourceSubtype}
            </Badge>
          )}
        </div>
      </div>

      {shouldShowCompleted || step === 'completed' ? (
        /* State 3: Final State (Inventory Updated - Matches Screenshot) */
        <div className="space-y-5">
          {/* Card 1: Repair Outcome */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 border-l-4 border-l-emerald-500 shadow-sm space-y-4">
            <h3 className="text-xs font-semibold text-gray-500 tracking-wider">
              Repair Outcome
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6">
              <ValueLabel label="Fault Type" value={outcomeFaultType} />
              <ValueLabel label="Repair Method" value={outcomeRepairMethod} />
            </div>

            {outcomePartsReplaced && outcomePartsReplaced !== 'NA' && outcomePartsReplaced !== '—' && (
              <div>
                <ValueLabel label="Parts Replaced" value={outcomePartsReplaced} />
              </div>
            )}

            <div>
              <ValueLabel label="Fault Description" value={outcomeFaultDescription} />
            </div>

            {outcomeTechnicianNotes && outcomeTechnicianNotes !== 'NA' && outcomeTechnicianNotes !== '—' && (
              <div>
                <ValueLabel label="Technician Notes" value={outcomeTechnicianNotes} />
              </div>
            )}
          </div>

          {/* Card 2: Inventory Updated */}
          <div className="bg-emerald-50/40 border border-emerald-100 rounded-xl p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <h4 className="text-sm font-semibold text-emerald-900">
                Inventory Updated
              </h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-y-3 gap-x-6 pt-1">
              <ValueLabel
                label="Resource"
                value={
                  <span className="font-semibold text-amber-700">
                    {activeResourceCode}
                  </span>
                }
              />
              <ValueLabel
                label="Returned To"
                value={
                  <span className="font-semibold text-gray-900">
                    {outcomeReturnedTo}
                  </span>
                }
              />
              <ValueLabel
                label="Physical Status"
                value={
                  <Badge
                    variant="outline"
                    className="bg-emerald-100 text-emerald-700 border-emerald-200 font-medium text-xs px-2.5 py-0.5 rounded-full"
                  >
                    {outcomePhysicalStatus}
                  </Badge>
                }
              />
            </div>
          </div>
        </div>
      ) : !isStarted ? (
        <div className="bg-amber-50/50 border border-amber-200/70 rounded-xl p-10 text-center space-y-2">
          <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-2">
            <Clock className="w-4 h-4" />
          </div>
          <h4 className="text-sm font-semibold text-amber-900">Operation Not Started</h4>
          <p className="text-xs text-amber-700">
            Start the operation to begin recording repair details.
          </p>
        </div>
      ) : step === 'form' ? (
        /* State 1: Form Editing */
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
          {/* Header with Icon */}
          <div className="flex items-start gap-3 border-b border-gray-100 pb-4">
            <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <Wrench className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Repair Details</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Record fault findings and actions taken to complete
              </p>
            </div>
          </div>

          <DynamicForm
            form={repairForm}
            fields={initialFields}
            onSubmit={handleInitialSubmit}
            gridColumns={2}
            config={{ showSubmitButton: false, showResetButton: false }}
            renderCustomFooter={() => <></>}
          />

          {/* Status Inventory Info Notice */}
          <div className="flex items-center justify-between p-3.5 bg-gray-50/80 border border-gray-100 rounded-lg text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-400" />
              <span>Physical status in inventory will be marked as</span>
            </div>
            <Badge
              variant="outline"
              className="bg-emerald-50 text-emerald-700 border-emerald-200 font-medium text-xs px-2.5 py-0.5 rounded-full"
            >
              Repaired
            </Badge>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              type="button"
              onClick={repairForm.handleSubmit(handleInitialSubmit)}
              disabled={isCompleting}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium px-5 py-2.5 rounded-xl shadow-sm text-sm"
            >
              {isCompleting ? 'Saving...' : 'Mark Repair as Complete'}
            </Button>
          </div>
        </div>
      ) : step === 'inventory' ? (
        /* State 2: After Form Submission -> Return to Inventory */
        <div className="space-y-5">
          {/* Card 1: Repair Outcome */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 border-l-4 border-l-emerald-500 shadow-sm space-y-4">
            <h3 className="text-xs font-semibold text-gray-500 tracking-wider">
              Repair Outcome
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-y-4 gap-x-6">
              <ValueLabel label="Fault Type" value={repairOutcome.faultType} />
              <ValueLabel label="Repair Method" value={repairOutcome.repairMethod} />
              {repairOutcome.partsReplaced && repairOutcome.partsReplaced !== '—' && (
                <ValueLabel label="Parts Replaced" value={repairOutcome.partsReplaced} />
              )}
            </div>

            <div>
              <ValueLabel label="Fault Description" value={repairOutcome.faultDescription} />
            </div>

            {repairOutcome.technicianNotes && (
              <div>
                <ValueLabel label="Technician Notes" value={repairOutcome.technicianNotes} />
              </div>
            )}
          </div>

          {/* Card 2: Return to Inventory */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3 border-b border-gray-100 pb-4">
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <Package className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Return to Inventory</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Select a warehouse and set the physical status after repair
                </p>
              </div>
            </div>

            {/* Resource Status Transition Bar */}
            <div className="bg-gray-50/80 p-3.5 rounded-xl border border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs">
                <Key className="w-4 h-4 text-gray-500" />
                <span className="font-semibold text-gray-900">{repairOutcome.resourceCode}</span>
                <Badge
                  variant="outline"
                  className="bg-gray-100 text-gray-700 border-gray-200 text-[11px] font-medium"
                >
                  {repairOutcome.resourceType}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <Badge
                  variant="outline"
                  className="bg-emerald-50 text-emerald-700 border-emerald-200 font-medium text-xs px-2.5 py-0.5 rounded-full"
                >
                  Repaired
                </Badge>
              </div>
            </div>

            {/* DynamicForm for Warehouse selection */}
            <div className="space-y-4">
              <DynamicForm
                form={inventoryForm}
                fields={inventoryFields}
                onSubmit={handleInventorySubmit}
                gridColumns={2}
                config={{ showSubmitButton: false, showResetButton: false }}
                renderCustomFooter={() => <></>}
              />

              {/* Warehouse Location Info Box */}
              {selectedWarehouse && (
                <div className="p-3.5 bg-blue-50/60 border border-blue-100 rounded-lg flex items-start gap-2.5">
                  <Box className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-xs font-semibold text-blue-800">
                      {selectedWarehouse.label}
                    </h5>
                    <p className="text-[11px] text-blue-600 mt-0.5">
                      {selectedWarehouse.location}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Action Button */}
            <Button
              type="button"
              onClick={inventoryForm.handleSubmit(handleInventorySubmit)}
              disabled={isReturning}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2 py-3 rounded-xl shadow-sm font-medium text-sm"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>{isReturning ? 'Saving...' : 'Mark as Repaired in Inventory'}</span>
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
export default Repair;
